import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { useMemo, useCallback } from 'react';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { useFetchCatalogFields } from 'modules/catalogFieldTypes/hooks';
import { FieldType, CatalogFieldDetails } from 'modules/domains';
import { isDefined } from 'utils/tsHelpers';
import { FieldOption } from 'modules/queryBuilder/legacyQueryBuilder/types';
import { BaseOption } from 'ui/selects';
import { useCatalogSelectionContext } from '../contexts/CatalogSelectionContext';
import { useAngularCatalogQueryContext } from '../../contexts/AngularCatalogQueryContext';

type ContactPropertyName = FieldOption['value'];
export type CatalogFieldBaseOption = BaseOption<ContactPropertyName> & {
  fieldType: FieldType;
};

export const catalogMatchKeyId = 'Key value';
const catalogKeyFieldType = FieldType.STRING;
const catalogKeySelection: CatalogFieldDetails = {
  fieldType: catalogKeyFieldType,
};

const getUniqueIdentifier = (name = '') => `catalogField: ${name}`;
const isMatchingProperty = (id: string) => (baseOption: CatalogFieldBaseOption) =>
  baseOption.value === id;
const findMatchingProperty = (baseOptions: CatalogFieldBaseOption[]) => (id: ContactPropertyName) =>
  baseOptions.find(isMatchingProperty(id));

const getCatalogFieldDetails = (
  baseOption?: CatalogFieldBaseOption,
): CatalogFieldDetails | undefined =>
  !isDefined(baseOption)
    ? baseOption
    : { field: baseOption.value, fieldType: baseOption.fieldType };

const extractCatalogField: (
  selection: option.Option<CatalogFieldDetails>,
) => option.Option<CatalogFieldDetails> = flow(
  option.map(({ field, fieldType }) => ({
    field: field?.replace('catalogField: ', ''),
    fieldType,
  })),
);

const createBaseOptions = (
  fieldType: FieldType,
  hasCatalogKeyOption: boolean,
): ((data: FieldOption[]) => CatalogFieldBaseOption[]) =>
  flow(
    array.filter(({ type }) => type === fieldType),
    array.map(({ name, type }) => ({
      id: getUniqueIdentifier(name),
      value: getUniqueIdentifier(name),
      label: name,
      Suffix: type,
      isDisabled: false,
      fieldType: type,
    })),
    array.prepend({
      id: 'catalogKey',
      label: catalogMatchKeyId,
      value: catalogMatchKeyId,
      Suffix: 'key',
      isDisabled: !hasCatalogKeyOption,
      fieldType: FieldType.STRING as FieldType, // don't know why I need to cast here? FieldType.String should still be type FieldType???
    }),
  );

const createGetSelection = (
  baseOptions: CatalogFieldBaseOption[],
): ((selected: string[]) => option.Option<CatalogFieldDetails>) =>
  flow(
    array.map(findMatchingProperty(baseOptions)),
    array.map(getCatalogFieldDetails),
    array.map(option.fromNullable),
    array.compact,
    array.head,
  );

const isSelectedMatchingOnKey = (selectedOption: string) => selectedOption === catalogMatchKeyId;

const getCatalogMatchOnKey = flow(
  array.filter(isSelectedMatchingOnKey),
  array.map(option.fromNullable),
  array.compact,
  array.head,
  option.isSome,
);

const findMatchingCatalogField =
  (baseOptions: CatalogFieldBaseOption[]) => (catalogFieldDetails: CatalogFieldDetails) =>
    baseOptions.find(({ value }) => value === getUniqueIdentifier(catalogFieldDetails.field));

// takes in catalogFieldDetails returns value reformatted again
const getDefaultCatalogFieldValue =
  (catalogFieldDetails: option.Option<CatalogFieldDetails>) =>
  (baseOptions: CatalogFieldBaseOption[]): string =>
    pipe(
      catalogFieldDetails,
      option.map(findMatchingCatalogField(baseOptions)),
      option.map((baseOption) => baseOption?.value ?? ''),
      option.getOrElse(() => ''),
    );

const getCatalogFieldTypeError =
  (catalogFieldDetails: option.Option<CatalogFieldDetails>) =>
  (searchFieldType: FieldType): boolean =>
    pipe(
      catalogFieldDetails,
      option.map(({ fieldType }) => fieldType !== searchFieldType),
      option.getOrElse(() => false as boolean),
    );

export const useCatalogFieldSelection = () => {
  const { changeFieldDetails, catalog, catalogFieldDetails } = useCatalogContext();
  const { catalogQueryDetails } = useAngularCatalogQueryContext();
  const { update, isMatchingOnKey } = useCatalogSelectionContext();
  const { data, isLoading } = useFetchCatalogFields(option.toUndefined(catalog)?.name);

  // create options for catalog field selector
  const searchFieldType = catalogQueryDetails?.fieldType ?? FieldType.UNDEFINED;
  const hasCatalogKeyOption = catalogKeyFieldType === searchFieldType;
  const isCatalogNone = option.isNone(catalog);
  const options = createBaseOptions(searchFieldType, hasCatalogKeyOption)(data);
  const hasCatalogProperties = options.length > 1; // options should always have one option -- Key value
  const hasCatalogFieldTypeError = getCatalogFieldTypeError(catalogFieldDetails)(searchFieldType);
  const hasInvalidFieldOptions = !hasCatalogKeyOption && !hasCatalogProperties;
  const isFieldSelectorDisabled = isCatalogNone || hasInvalidFieldOptions;

  const defaultValues = useMemo(() => {
    const catalogFieldValue = getDefaultCatalogFieldValue(catalogFieldDetails)(options);
    return isMatchingOnKey ? [catalogMatchKeyId] : [catalogFieldValue];
  }, [isMatchingOnKey, catalogFieldDetails, options]);

  const getSelection = createGetSelection(options);

  const onSelect = useCallback(
    (selected: string[]) => {
      // only execute if select is made
      if (selected.length) {
        const selection = getSelection(selected);
        const match = getCatalogMatchOnKey(selected);
        update(match);
        changeFieldDetails(
          match ? option.some(catalogKeySelection) : extractCatalogField(selection),
        );
      }
    },
    [catalogFieldDetails, options],
  );

  return {
    onSelect,
    options,
    isLoading,
    defaultValues,
    isFieldSelectorDisabled,
    hasCatalogFieldTypeError,
    hasInvalidFieldOptions,
    isCatalogNone,
  };
};
